import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../../commonComponents/translate';
import BasicPortlet from '../../../../commonComponents/portlets/BasicPortlet';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import AmPieChart from '../../../../commonComponents/charts/AmPieChart';
import TrophyPortlet from './TrophyPortlet';
import { TROPHY_CONFIG } from '../../../../services/utilityFunctions';
import TrophyModal from './TrophyModal';
import performanceProp from '../../propTypes/performanceProp';
import chartsProp from '../../propTypes/chartsProp';
import ServicesContext from '../../../../contextProviders/ServicesContext';
import CertificatePortlet from './CertificatePortlet';
import certificateProp from '../../../academy/propTypes/certificateProp';
import Notifications from '../../../../services/notifications';
import Loader from '../../../../services/loader';
import { useModal } from '../../../../services/customHooks';

const setSkillsTrophyConfig = statsParam => {
  const stats = statsParam || { skillsTrophies: { typesSkills: {} } };

  return Object.entries(TROPHY_CONFIG)
    .filter(([name]) => name.includes('e-learning_'))
    .map(([name, trophy]) => ({
      ...trophy,
      name,
      stats: stats.skillsTrophies.typesSkills[name],
    }));
};

function TabAcademyPerformance({ t, stats, certificates }) {
  const loaderSkills = useRef(new Loader('#skills-loader'));
  const loaderCourseStats = useRef(new Loader('#course-stats-loader'));
  const [isLoading, setLoading] = useState(false);
  useEffect(() => {
    if (loaderSkills.current) {
      if (isLoading) {
        loaderSkills.current.show();
      } else {
        loaderSkills.current.hide();
      }
    }
    if (loaderCourseStats.current) {
      if (isLoading) {
        loaderCourseStats.current.show();
      } else {
        loaderCourseStats.current.hide();
      }
    }
  }, [isLoading]);
  /** INITIAL STATE */
  const { loggedUser } = useContext(LoggedUserContext);
  const [charts, setCharts] = useState({});
  // Trophy modal
  const [modalState, setModalState] = useModal({
    show: false,
    data: null,
  });
  const showModal = trophy => setModalState({ show: true, data: trophy });
  const services = useContext(ServicesContext);
  const academyIsAvailable =
    services && services.academy && services.academy.enabled === true;
  const certificatesIsAvailable =
    services && services.certificates && services.certificates.enabled === true;

  /** API REQUESTS */
  const getSkills = () => {
    setLoading(true);
    const params = { user: loggedUser._id };
    axios
      .get('/api/analytics/academy/course/skills', {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setCharts(prevState => ({ ...prevState, skills: response.data }));
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(
          err,
          'profile.performance.academy.error.skills',
        );
      });
  };
  const getCoursesStats = () => {
    setLoading(true);
    const params = { user: loggedUser._id };
    axios
      .get('/api/analytics/academy/course/status', {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setCharts(prevState => {
          // Modified data so we can get the right values for chart status -> total
          const modifiedData = Object.entries(response.data).map(
            ([status, total]) => ({
              name:
                status === 'waiting'
                  ? t('profile.performance.course.status.inactive')
                  : t(`profile.performance.course.status.${status}`),
              total,
            }),
          );
          return { ...prevState, coursesStats: modifiedData };
        });
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(
          err,
          'profile.performance.academy.error.courseStatus',
        );
      });
  };
  useEffect(() => {
    getCoursesStats();
    getSkills();
  }, []);

  /** SKILLS TROPHY */
  const skillsTrophyConfig = setSkillsTrophyConfig(stats.performance);
  const hideModal = () => setModalState({ show: false, data: null });
  const getTrophies = () => {
    const trophies = stats.charts.trophies || [];
    const skillTrophies = stats.charts.skillsTrophies || [];
    const allTrophies = trophies.concat(skillTrophies);
    return modalState.data && modalState.data.name
      ? allTrophies.filter(tr => tr.type === modalState.data.name)
      : allTrophies.filter(
          tr =>
            tr.type === 'custom' &&
            tr.trophyCustom &&
            tr.trophyCustom._id === modalState.data._id,
        );
  };

  /** RENDER COMPONENT */
  // CHARTS
  const getCharts = type => {
    if (type === 'courseStats') {
      return (
        <AmPieChart
          chartId="courseStatsId"
          containerClass="report-chart-content"
          stats={charts.coursesStats}
          options={{
            value: 'total',
            category: 'name',
          }}
        />
      );
    }
    if (type === 'skills') {
      return (
        <AmPieChart
          chartId="skillsId"
          containerClass="report-chart-content"
          stats={charts.skills}
          options={{
            value: 'finishedUsers',
            category: 'name',
          }}
        />
      );
    }
    return null;
  };
  return (
    <>
      {modalState.show && (
        <TrophyModal trophies={getTrophies()} onClose={hideModal} />
      )}
      <div className="tab-academy-performance">
        {academyIsAvailable && (
          <div id="course-stats-loader">
            <BasicPortlet title={t('profile.performance.course.status.title')}>
              {charts?.coursesStats ? (
                getCharts('courseStats')
              ) : (
                <div className="report-chart-content" />
              )}
            </BasicPortlet>
          </div>
        )}
        {academyIsAvailable && (
          <TrophyPortlet
            title={t('profile_skillsTrophies')}
            trophyConfig={skillsTrophyConfig}
            onSelect={showModal}
          />
        )}
        {academyIsAvailable && (
          <div id="skills-loader">
            <BasicPortlet title={t('profile.performance.academy.skills.title')}>
              {charts?.coursesStats ? (
                getCharts('skills')
              ) : (
                <div className="report-chart-content" />
              )}
            </BasicPortlet>
          </div>
        )}
        {certificatesIsAvailable && (
          <CertificatePortlet certificates={certificates} />
        )}
      </div>
    </>
  );
}

TabAcademyPerformance.propTypes = {
  t: PropTypes.func.isRequired,
  stats: PropTypes.shape({
    performance: PropTypes.shape(performanceProp.propType),
    charts: PropTypes.shape(chartsProp.propType),
  }).isRequired,
  certificates: PropTypes.arrayOf(PropTypes.shape(certificateProp.propType))
    .isRequired,
};

export default translate(TabAcademyPerformance);
